import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Textarea,
  Button,
  useDisclosure,
  FormControl,
  FormLabel,
  Spinner,
  Text,
  VStack,
  Flex,
  Input,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  ModalCloseButton,
} from "@chakra-ui/react";
import ArtifactPanel from "modules/artifacts/ArtifactPanel";
import SplitView from "components/common/SplitView";
import ChatContainer from "modules/chat/containers/Chat";
import { ArtifactOpenOpts, ChatMessage } from "modules/interfaces";
import { deleteChatSession, duplicateChatSession, fetchChatSession, saveSession } from "services/AgentChatService";
import {
  ArtifactProto,
  ChatClientType,
  chatClientTypeFromJSON,
  chatClientTypeToJSON,
  ExecutionStepProto,
} from "build-ts/chat_session";
import {
  artifactTypeFromJSON,
  ArtifactType,
  ChatMessageType,
  chatMessageTypeFromJSON,
  DarwinHeader,
  ExecutionStatusType,
} from "build-ts/common";
import showToast from "hooks/useCustomToast";
import { useArtifactStore } from "modules/artifacts/hooks/useArtifactStore";
import { useArtifactPanel } from "modules/artifacts/hooks/useArtifactPanel";
import { ArtifactKind, getArtifactHeaderName } from "modules/artifacts/utils/artifactUtils";
import CopyButton from "components/common/CopyButton";
import { useChatContext } from "context/ChatContext";
import { useUIContext } from "context/UIContext";
import { useURLContext } from "context/URLContext";
import { useUser } from "context/UserContext";
import ChatPanelHeaderContainer from "modules/chat/containers/ChatPanelHeader";
import { HeaderWithStats } from "services/MetadataServices";
import { fetchAnalystList } from "services/serviceUtils";
import { ScheduledJobProto, ScheduledJobType } from "build-ts/schedule";
import { listScheduledJobsByOwnerId } from "services/ScheduleServices";

interface ChatPageProps {
  systemPrompt?: string;
  // Eva flags, true when rendered from any two components of Eva (both Saved and Unsaved chat)
  isFromEva?: boolean;
  initialChatSessionHeader?: DarwinHeader; // Session Header from EvaPlayground
  /**
   * Called by ChatPage after actions that change the EVA session list (rename, delete, save).
   * Provided by EvaListContainer to refresh its local session list; unused for FRIDA chats.
   */
  onEvaSessionsRefresh?: () => void;
  // Scenario creation mode (when set, chat is used for scenario creation with analyst/scenario context)
  /** The analyst id we navigated from (used to set selected analyst) */
  initialAnalystId?: string;
  // Selected scenario ids from the chat menu
  selectedScenarioIds?: string[];
  /** If set, chat will initialize a session and send this as the first user message */
  initialPrompt?: string;
}

// This is used to clean the content of the chat messages from the ```markdown or ``` wrapper
const cleanContent = (raw: string): string => {
  // Remove ```markdown or ``` wrapper
  return raw.replace(/^```(?:markdown)?\n?/, "").replace(/\n?```$/, "");
};

const ChatPage: React.FC<ChatPageProps> = ({
  systemPrompt = "",
  isFromEva = false,
  initialChatSessionHeader,
  onEvaSessionsRefresh,
  initialAnalystId: initialAnalystIdProp,
  initialPrompt: initialPromptProp,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Initial prompt and analyst id from navigation state
  const navState = location.state as { initialPrompt?: string; analystId?: string } | null;
  const initialPrompt = initialPromptProp || navState?.initialPrompt;
  // Initial analyst id from navigation state — auto-send guard waits until selectedAnalyst matches this
  const initialAnalystId = navState?.analystId;
  const { id: chatSessionId } = useParams();
  const { defaultAnalystId, userId, defaultTimeZone } = useUser();
  const { chatAnalystId } = useURLContext();
  // Chat session state from ChatContext
  const { setCurrentChatId, currentSessionHeader, setCurrentSessionHeader } = useChatContext();

  // Navbar collapse state from UIContext (app-wide)
  const { setIsNavbarCollapsed } = useUIContext();
  // Check if this is a new chat route
  const isNewChatRoute = location.pathname === "/chat/new";
  const isEvaChatRoute = location.pathname.startsWith("/eva/chat");
  // analyst state
  const [analystsList, setAnalystsList] = useState<HeaderWithStats[]>([]);
  const [isLoadingAnalysts, setIsLoadingAnalysts] = useState(true);
  const [selectedAnalyst, setSelectedAnalyst] = useState<HeaderWithStats | null>(null);
  // Loading state for chat
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  // System prompt modal state
  const {
    isOpen: isSystemPromptModalOpen,
    onOpen: onSystemPromptModalOpen,
    onClose: onSystemPromptModalClose,
  } = useDisclosure();
  // Save chat modal state
  const { isOpen: isSaveModalOpen, onOpen: onSaveModalOpen, onClose: onSaveModalClose } = useDisclosure();
  const [saveChatTitle, setSaveChatTitle] = useState("");
  const [saveChatDescription, setSaveChatDescription] = useState("");
  // Scenario jobs for the chat (loaded on session init + updated after manual/agent saves)
  const [scenarioJobs, setScenarioJobs] = useState<ScheduledJobProto[]>([]);
  // Track the last session ID we actually started loading to prevent duplicate fetches
  // when dependencies like analystsList change reference without the session ID changing.
  const lastFetchedSessionIdRef = useRef<string | null>(null);
  // Effective analyst id for scenario operations (from props or loaded chat session)
  const [resolvedAnalystId, setResolvedAnalystId] = useState<string | undefined>(initialAnalystIdProp);
  // Detect scenario jobs saved by the agent via tool calls (tool name includes "save_job")
  // in the last message of the response.
  const [scenarioJobsSavedByTools, setScenarioJobsSavedByTools] = useState<ScheduledJobProto[]>([]);
  const [chatId, setChatId] = useState<string | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  // System prompt state - initialized from prop, can be updated from saved chat
  const [currentSystemPrompt, setCurrentSystemPrompt] = useState<string>(systemPrompt);
  // Chat is saved if session header has created_date
  const [isChatSaved, setIsChatSaved] = useState(false);
  // Artifact store: Map-based registry for artifact management
  const { artifactMap, registerArtifacts, updateArtifact, lookupByName, clearStore } = useArtifactStore();
  // Artifact panel: state + open-handlers
  const artifactPanel = useArtifactPanel({
    updateArtifact,
    onOpen: () => setIsNavbarCollapsed(true),
  });

  // Load analysts list
  useEffect(() => {
    const load = async () => {
      setIsLoadingAnalysts(true);
      const list = await fetchAnalystList(false);
      setAnalystsList(list);
      // Auto-select default analyst
      if (!isFromEva) {
        const defaultAnalyst = list.find(a => a.id === defaultAnalystId) || list[0] || null;
        setSelectedAnalyst(defaultAnalyst);
      }
      setIsLoadingAnalysts(false);
    };
    load();
  }, []);

  // Handle chatAnalystId from URL (when navigating from AnalystPage's Chat button)
  useEffect(() => {
    // Only process if chatAnalystId is in the current URL query params
    const urlHasChatAnalystId = location.search.includes("chatAnalystId");
    if (!urlHasChatAnalystId || !chatAnalystId || isFromEva) {
      return;
    }
    // Wait for analysts list to be loaded
    if (isLoadingAnalysts || analystsList.length === 0) {
      return;
    }
    // Find the analyst by ID
    const analyst = analystsList.find(a => a.id === chatAnalystId);
    if (analyst) {
      // Set the analyst and navigate to /chat/new to create a new session
      // Preserve initialPrompt so any auto-send message (e.g. from workflow buttons) is not lost
      const prevInitialPrompt = (location.state as { initialPrompt?: string } | null)?.initialPrompt;
      setSelectedAnalyst(analyst);
      navigate("/chat/new", { replace: true, state: { preserveAnalyst: true, initialPrompt: prevInitialPrompt } });
    } else {
      // Analyst not found - show error and navigate to /chat/new without selection
      showToast("Analyst not found", "", "error");
      navigate("/chat/new", { replace: true });
    }
  }, [chatAnalystId, analystsList, isLoadingAnalysts, isFromEva]);

  // Keep selectedAnalyst in sync with resolvedAnalystId for existing chats
  useEffect(() => {
    if (!resolvedAnalystId || analystsList.length === 0 || isNewChatRoute || isFromEva) return;

    const analyst = analystsList.find(a => a.id === resolvedAnalystId);
    if (analyst) {
      setSelectedAnalyst(analyst);
    }
  }, [resolvedAnalystId, analystsList, isNewChatRoute, isFromEva]);

  // Set chatId from initialChatSessionId when coming from EvaPlayground
  useEffect(() => {
    if (isFromEva && !isChatSaved && initialChatSessionHeader && !chatId) {
      setChatId(initialChatSessionHeader.id);
      setCurrentSessionHeader(initialChatSessionHeader);
    }
  }, [isFromEva, isChatSaved, initialChatSessionHeader, chatId, navigate]);

  // Effect to close artifacts panel and reset the artifact registry when loading a chat.
  // Runs whenever isLoadingChat flips to true (i.e. before fetchChatHistory fires),
  // ensuring the previous chat's artifacts don't bleed into the next one.
  useEffect(() => {
    if (isLoadingChat) {
      artifactPanel.closePanel();
      artifactPanel.clearAll();
      clearStore();
    }
  }, [isLoadingChat, clearStore]);

  // Update currentSystemPrompt when systemPrompt prop changes (for playground)
  useEffect(() => {
    if (systemPrompt) {
      setCurrentSystemPrompt(systemPrompt);
    }
  }, [systemPrompt]);

  // Merge agent-saved workflow into existing workflow jobs, artifact store, selectedArtifacts, and chatMessages.
  useEffect(() => {
    if (scenarioJobsSavedByTools.length === 0) return;
    setScenarioJobs(prev => {
      let updated = [...prev];
      for (const job of scenarioJobsSavedByTools) {
        if (!job.header?.id) continue;
        const idx = updated.findIndex(j => j.header?.id === job.header!.id);
        if (idx >= 0) {
          updated = [...updated.slice(0, idx), job, ...updated.slice(idx + 1)];
        } else {
          updated = [...updated, job];
        }
      }
      return updated;
    });
    for (const job of scenarioJobsSavedByTools) {
      updateArtifact({
        artifact_type: ArtifactType.SCENARIO_JOB,
        scenario_job: job,
      } as ArtifactProto);
    }
    for (const job of scenarioJobsSavedByTools) {
      artifactPanel.updateJobInPanel(job);
    }
    setChatMessages(prev =>
      prev.map(msg => {
        if (!msg.artifacts) return msg;
        const updatedArtifacts = msg.artifacts.map(a => {
          if (artifactTypeFromJSON(a.artifact_type) !== ArtifactType.SCENARIO_JOB) return a;
          const match = scenarioJobsSavedByTools.find(j => j.header?.id && j.header.id === a.scenario_job?.header?.id);
          return match ? { ...a, scenario_job: match } : a;
        });
        return { ...msg, artifacts: updatedArtifacts };
      })
    );
  }, [scenarioJobsSavedByTools]);

  // Effect to handle URL changes: /chat/new or /chat/:id or /eva/chat/:id (skip in scenario creation mode)
  useEffect(() => {
    // Handle EVA Chat case
    if (isEvaChatRoute && chatSessionId && chatSessionId !== lastFetchedSessionIdRef.current) {
      lastFetchedSessionIdRef.current = chatSessionId;
      setChatId(chatSessionId);
      setIsLoadingChat(true);
      fetchChatHistory(chatSessionId)
        .catch(err => {
          console.error("fetchChatHistory failed:", err);
          setChatMessages([]);
        })
        .finally(() => setIsLoadingChat(false));
      return;
    }

    // Handle /chat/new route - initialize a new chat session
    if (isNewChatRoute) {
      lastFetchedSessionIdRef.current = null;
      setChatMessages([]);
      clearStore();
      artifactPanel.clearAll();
      artifactPanel.closePanel();
      setChatId(undefined);
      setIsChatSaved(false);
      setCurrentSessionHeader(undefined);
      setCurrentChatId("");
      setScenarioJobs([]);
      clearStore();
      artifactPanel.clearAll();
      setScenarioJobsSavedByTools([]);
      return;
    }

    // Handle /chat/:id route - load existing chat session
    if (
      chatSessionId &&
      chatSessionId !== lastFetchedSessionIdRef.current &&
      chatSessionId !== currentSessionHeader?.id &&
      !isEvaChatRoute
    ) {
      lastFetchedSessionIdRef.current = chatSessionId;

      // If chatId is already set to this session it means Chat.tsx just created it
      // (new-chat flow: Step 1 sets chatId + navigates before the AI response arrives).
      // The session is already live in memory with the user's optimistic messages, so
      // fetching from the DB (which has 0 messages at this point) would wipe everything.
      if (chatId === chatSessionId) {
        return;
      }

      setIsLoadingChat(true);
      fetchChatHistory(chatSessionId)
        .catch(err => {
          console.error("fetchChatHistory failed:", err);
          setChatMessages([]);
        })
        .finally(() => setIsLoadingChat(false));
    }
  }, [isNewChatRoute, chatSessionId, initialChatSessionHeader]);

  // Select the correct analyst when /chat/new is active and the analysts list loads.
  // Kept separate so that analystsList loading never re-triggers the state-clear block above.
  useEffect(() => {
    if (!isNewChatRoute || isFromEva) return;
    if (analystsList.length === 0) return;
    // chatAnalystId URL param takes precedence — the dedicated chatAnalystId effect owns that path
    const hasChatAnalystIdParam = location.search.includes("chatAnalystId");
    if (location.state?.preserveAnalyst || hasChatAnalystIdParam) return;
    const stateAnalystId = (location.state as { analystId?: string } | null)?.analystId;
    const target = stateAnalystId
      ? analystsList.find(a => a.id === stateAnalystId)
      : analystsList.find(a => a.id === defaultAnalystId) || analystsList[0];
    setSelectedAnalyst(target ?? null);
  }, [analystsList, isNewChatRoute]);

  // Handle analyst selection
  const handleAnalystChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const analystId = e.target.value;
    if (!isNewChatRoute) {
      // Existing chat: open a new tab with the chosen analyst pre-selected.
      // Do NOT update selectedAnalyst here — the current tab's dropdown must
      // continue showing the analyst that belongs to this conversation.
      // Reuse the existing chatAnalystId param so the new tab's effect (which
      // waits for analystsList to load) handles selection correctly.
      window.open(`/chat/new?chatAnalystId=${analystId}`, "_blank");
      return;
    }
    // New chat screen: just update the selection in place, no navigation needed.
    const analyst = analystsList.find(a => a.id === analystId);
    setSelectedAnalyst(analyst || null);
  };

  // Set of job header ids already persisted (for Saved vs Draft badge on ScenarioArtifactCard)
  const savedJobIds = useMemo(() => {
    const ids = new Set<string>();
    scenarioJobs.forEach(job => {
      if (job.header?.id) ids.add(job.header.id);
    });
    return ids;
  }, [scenarioJobs]);

  // Stable wrapper forwarding to the panel hook; chatId is passed as a param so the
  // useCallback in useArtifactPanel never captures a stale chatId.
  // Prefer the already-hydrated artifact from the store over the stale original from
  // message data — this prevents redundant populate calls on every re-click.
  const handleArtifactClick = useCallback(
    (artifact: ArtifactProto, opts?: ArtifactOpenOpts) => {
      const latest = lookupByName(getArtifactHeaderName(artifact), opts?.kind as ArtifactKind | undefined) ?? artifact;
      artifactPanel.openArtifact(latest, chatId, opts?.activeTab, opts?.subResourceName);
    },
    [chatId, lookupByName]
  );

  // Populate a parent artifact in-place (for Inputs tab inline rendering) without opening a new tab.
  // Resolve the latest version from the store so populateSourceArtifact's already-hydrated
  // guard (`data_json` check) fires correctly and skips redundant network calls.
  const handlePopulateInput = useCallback(
    (artifact: ArtifactProto, opts?: ArtifactOpenOpts): Promise<void> => {
      const latest = lookupByName(getArtifactHeaderName(artifact), opts?.kind as ArtifactKind | undefined) ?? artifact;
      return artifactPanel.populateSourceArtifact(latest, chatId);
    },
    [chatId, lookupByName]
  );

  const handleToggleExpand = useCallback(() => {
    artifactPanel.setIsExpanded(v => !v);
  }, []);

  // Function to fetch the chat history - new darwin
  const fetchChatHistory = async (sessionId: string) => {
    // Fetch header only first to check client type — no messages, no transaction init.
    const headerResponse = await fetchChatSession(sessionId, false, false);
    const clientType = chatClientTypeFromJSON(headerResponse?.session_header?.type);
    const targetClientType = isFromEva ? ChatClientType.EVA : ChatClientType.FRIDA;
    const isMatchingType = clientType === targetClientType;

    let chatResponse;
    if (!isMatchingType) {
      // Client type doesn't match the current context — duplicate with the correct type.
      // e.g. FRIDA session opened in EVA context → duplicate as EVA, and vice versa.
      const duplicated = await duplicateChatSession({
        originalSessionId: sessionId,
        currentUserId: userId ?? "",
        clientType: chatClientTypeToJSON(targetClientType),
        save: false,
        initTransaction: true,
      });
      if (!duplicated?.session_header?.id) return;
      window.history.replaceState(null, "", `${isFromEva ? "/eva" : ""}/chat/${duplicated.session_header.id}`);
      chatResponse = duplicated;
      if (duplicated.analyst_id) {
        const jobs = await listScheduledJobsByOwnerId(duplicated.analyst_id);
        if (jobs && jobs.length > 0) {
          setScenarioJobs(jobs.filter((job: ScheduledJobProto) => job.type === ScheduledJobType.SCENARIO_JOB));
        }
      }
    } else {
      chatResponse = await fetchChatSession(sessionId, true, true);
      const analystId = chatResponse?.analyst_id;
      if (analystId) {
        const jobs = await listScheduledJobsByOwnerId(analystId);
        if (jobs && jobs.length > 0) {
          setScenarioJobs(jobs.filter((job: ScheduledJobProto) => job.type === ScheduledJobType.SCENARIO_JOB));
        }
      }
    }

    if (chatResponse?.session_header?.id) {
      setChatId(chatResponse.session_header.id);
      setCurrentChatId(chatResponse.session_header.id);
      setCurrentSessionHeader(chatResponse.session_header);
      if (chatResponse.session_header.created_date) {
        setIsChatSaved(true);
      }
    }

    // Resolve analyst id from existing chat session; keep it in sync when switching chats
    if (chatResponse?.analyst_id && chatResponse.analyst_id !== resolvedAnalystId) {
      setResolvedAnalystId(chatResponse.analyst_id);
    }

    // Check the status of the chat
    if (chatResponse?.status) {
      if (chatResponse.status === ExecutionStatusType.ERROR_STATE) {
        showToast("Error in Chat.", "Please start a new chat.", "error");
      }
    }

    // Extract system prompt and context from first SYSTEM / CONTEXT messages for saved chats
    let systemPromptFromMessage = "";
    const convertedMessages: ChatMessage[] = [];
    const collectedArtifacts: ArtifactProto[] = [];
    let pendingArtifacts: ArtifactProto[] = [];
    let pendingExecutionSteps: ExecutionStepProto[] = [];
    let langgraphMessageJsonList: string[] = [];
    // End-timestamp of the most recent message before the current REASONING block.
    // Used to compute the first reasoning step's duration on reload.
    let prevMsgEndMs: number | undefined;

    for (const msg of chatResponse.messages) {
      const messageType = chatMessageTypeFromJSON(msg.message_type);

      // Extract system prompt from first SYSTEM message
      if (messageType === ChatMessageType.SYSTEM && msg.content && !systemPromptFromMessage) {
        systemPromptFromMessage = msg.content;
      } else if (messageType === ChatMessageType.HUMAN && msg.content) {
        // Capture the human message timestamp so the first reasoning group in
        // the following AI turn correctly shows how long the model thought.
        prevMsgEndMs = msg.timestamp_ms ? Number(msg.timestamp_ms) : undefined;
        convertedMessages.push({
          author: "user",
          content: cleanContent(msg.content),
          userTimestampMs: msg.timestamp_ms,
        });
      } else if (messageType === ChatMessageType.REASONING) {
        // prevMsgEndMs is already set from the human message above.

        const steps = msg.execution_steps;
        pendingArtifacts = steps.flatMap(s => s.artifacts);
        pendingExecutionSteps = steps;
        langgraphMessageJsonList =
          msg.langgraph_message_json_list && msg.langgraph_message_json_list.length > 0
            ? msg.langgraph_message_json_list
            : [];

        for (const artifact of pendingArtifacts) {
          collectedArtifacts.push(artifact);
        }
      } else if (messageType === ChatMessageType.RESPONSE && msg.content) {
        convertedMessages.push({
          author: "ai",
          content: cleanContent(msg.content),
          artifacts: pendingArtifacts.length > 0 ? pendingArtifacts : undefined,
          executionSteps: pendingExecutionSteps.length > 0 ? pendingExecutionSteps : undefined,
          prevMessageEndTimestampMs: prevMsgEndMs,
          ls_trace_url: msg.ls_trace_url,
          langgraph_message_json_list: langgraphMessageJsonList,
        });
        pendingArtifacts = [];
        pendingExecutionSteps = [];
        langgraphMessageJsonList = [];
      } else if (messageType === ChatMessageType.RESPONSE && msg.response_message) {
        convertedMessages.push({
          author: "ai",
          content: "",
          responseMessage: msg.response_message,
          artifacts: pendingArtifacts.length > 0 ? pendingArtifacts : undefined,
          executionSteps: pendingExecutionSteps.length > 0 ? pendingExecutionSteps : undefined,
          prevMessageEndTimestampMs: prevMsgEndMs,
          ls_trace_url: msg.ls_trace_url,
          langgraph_message_json_list: langgraphMessageJsonList,
        });
        pendingArtifacts = [];
        pendingExecutionSteps = [];
        langgraphMessageJsonList = [];
      }
    }
    // Update system prompt and context from API if found (for saved chats).
    // Always call setCurrentContext so stale context from a previous session is cleared
    // when the new session has no CONTEXT message.
    if (systemPromptFromMessage) {
      setCurrentSystemPrompt(systemPromptFromMessage);
    }
    setChatMessages(convertedMessages);
    // Register all collected artifacts into the store (deduplication handled by registerArtifacts).
    registerArtifacts(...collectedArtifacts);
  };

  return (
    <Box height="100%" width="100%" overflow="hidden" position="relative" display="flex" flexDirection="column">
      {/* Main Content Area */}
      <Box flex={1} width="100%" transition="margin-left 0.3s ease-in-out, width 0.3s ease-in-out" overflow="hidden">
        <SplitView
          leftPanel={
            <Box height="100%" width="100%" bg="bg.surface" overflow="hidden" display="flex" flexDirection="column">
              {currentSessionHeader && (
                <ChatPanelHeaderContainer
                  chatId={currentSessionHeader?.id || ""}
                  sessionHeader={currentSessionHeader}
                  setSessionHeader={setCurrentSessionHeader}
                  onAfterRename={() => {
                    if (isFromEva) onEvaSessionsRefresh?.();
                  }}
                  onDelete={() => {
                    if (!currentSessionHeader?.id) return;
                    const deletedId = currentSessionHeader.id;
                    deleteChatSession(deletedId).then(() => {
                      window.dispatchEvent(
                        new CustomEvent("chat:session-deleted", { detail: { sessionId: deletedId } })
                      );
                      if (isFromEva) {
                        onEvaSessionsRefresh?.();
                        navigate("/eva/agents");
                      } else {
                        navigate("/chat/new");
                      }
                    });
                  }}
                  onToggleArtifacts={() => artifactPanel.setIsVisible(v => !v)}
                  isArtifactsPanelVisible={artifactPanel.isVisible}
                  analystsList={analystsList}
                  selectedAnalyst={selectedAnalyst}
                  scenarioJobs={scenarioJobs}
                  isFromEva={isFromEva}
                  onViewPrompt={isFromEva ? onSystemPromptModalOpen : undefined}
                  isChatSaved={isChatSaved}
                  hasChatMessages={chatMessages.length > 0}
                  onSaveChat={isFromEva ? onSaveModalOpen : undefined}
                />
              )}
              <Box flex={1} overflow="hidden">
                <ChatContainer
                  userId={userId}
                  chatMessages={chatMessages}
                  chatId={chatId}
                  setChatId={setChatId}
                  setChatMessages={setChatMessages}
                  isLoadingChat={isLoadingChat}
                  sessionId={isFromEva && !isChatSaved ? initialChatSessionHeader?.id : chatSessionId}
                  onArtifactClick={handleArtifactClick}
                  selectedAnalyst={selectedAnalyst}
                  isFromEva={isFromEva}
                  cleanContent={cleanContent}
                  analystsList={analystsList}
                  isLoadingAnalysts={isLoadingAnalysts}
                  onAnalystChange={handleAnalystChange}
                  isArtifactsPanelVisible={artifactPanel.isVisible}
                  registerArtifacts={registerArtifacts}
                  lookupByName={lookupByName}
                  artifactMap={artifactMap}
                  isChatSaved={isChatSaved}
                  initialPrompt={initialPrompt}
                  initialAnalystId={initialAnalystId}
                  scenarioJobs={scenarioJobs}
                  setScenarioJobs={setScenarioJobs}
                  setScenarioJobsSavedByTools={setScenarioJobsSavedByTools}
                />
              </Box>
            </Box>
          }
          rightPanel={
            <ArtifactPanel
              selectedArtifacts={artifactPanel.selectedArtifacts}
              isLoadingArtifact={artifactPanel.isLoadingArtifact}
              onClosePanel={artifactPanel.closePanel}
              onClearAll={artifactPanel.clearAll}
              onRemoveArtifact={artifactPanel.removeArtifact}
              savedJobIds={savedJobIds}
              onPopulateInput={handlePopulateInput}
              activateArtifact={artifactPanel.activateArtifact}
              artifactMap={artifactMap}
              lookupFn={lookupByName}
              onArtifactClick={handleArtifactClick}
              isArtifactExpanded={artifactPanel.isExpanded}
              onToggleExpand={handleToggleExpand}
              onArtifactTabViewChange={artifactPanel.updateViewState}
              defaultTimeZone={defaultTimeZone}
              chatId={chatId}
            />
          }
          isRightPanelVisible={artifactPanel.isVisible}
          defaultRightWidth={45}
          expandRightPanel={artifactPanel.isExpanded}
        />
      </Box>

      {/* View prompt modal: System prompt + Context tabs */}
      <Modal isOpen={isSystemPromptModalOpen} onClose={onSystemPromptModalClose} size="4xl">
        <ModalOverlay />
        <ModalContent maxH="85vh">
          <ModalHeader>View prompt</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} overflow="hidden" display="flex" flexDirection="column">
            <Tabs size="sm" flex={1} display="flex" flexDirection="column" minH={0}>
              <TabList>
                <Tab>System prompt</Tab>
                <Tab>Context</Tab>
              </TabList>
              <TabPanels flex={1} overflow="auto" minH={0}>
                <TabPanel px={0} pt={3}>
                  <Flex justify="flex-end" mb={2}>
                    <CopyButton value={currentSystemPrompt} />
                  </Flex>
                  <Textarea
                    value={currentSystemPrompt}
                    readOnly
                    minH="400px"
                    resize="vertical"
                    bg="gray.50"
                    border="1px solid"
                    borderColor="gray.200"
                    fontFamily="mono"
                    fontSize="sm"
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Save Chat Modal */}
      <Modal isOpen={isSaveModalOpen} onClose={onSaveModalClose} size="md" closeOnOverlayClick={!isSaving}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Save Chat</ModalHeader>
          {!isSaving && <ModalCloseButton />}
          <ModalBody>
            {isSaving ? (
              <VStack spacing={4} py={6}>
                <Spinner size="lg" color="green.500" thickness="3px" />
                <Text color="gray.600" fontSize="md">
                  Saving your chat...
                </Text>
              </VStack>
            ) : (
              <>
                <FormControl>
                  <FormLabel>Chat Title</FormLabel>
                  <Input
                    placeholder="Enter a title for this chat..."
                    value={saveChatTitle}
                    onChange={e => setSaveChatTitle(e.target.value)}
                    autoFocus
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Chat Description</FormLabel>
                  <Input
                    placeholder="Enter a description for this chat..."
                    value={saveChatDescription}
                    onChange={e => setSaveChatDescription(e.target.value)}
                  />
                </FormControl>
              </>
            )}
          </ModalBody>
          {!isSaving && (
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onSaveModalClose}>
                Cancel
              </Button>
              <Button
                colorScheme="green"
                isDisabled={!saveChatTitle.trim()}
                onClick={async () => {
                  if (!chatId || !saveChatTitle.trim()) return;
                  setIsSaving(true);
                  try {
                    // Save with title in single API call
                    await saveSession(chatId, saveChatTitle.trim(), saveChatDescription?.trim());
                    onSaveModalClose();
                    setSaveChatTitle("");
                    setSaveChatDescription("");
                    setCurrentSessionHeader(undefined);
                    setIsChatSaved(true);
                    if (isFromEva) {
                      onEvaSessionsRefresh?.();
                      if (chatId) {
                        navigate(`/eva/chat/${chatId}`);
                      }
                    }
                  } finally {
                    setIsSaving(false);
                  }
                }}
              >
                Save
              </Button>
            </ModalFooter>
          )}
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ChatPage;
